import { useCallback, useState, type MouseEvent, type RefObject } from "react"

import type WaveSurfer from "wavesurfer.js"

import { TRACKING_EVENTS, useAnalytics } from "@/lib/analytics"
import { formatTime } from "@/lib/audio-player-utils"

import type { AudioMarker } from "./AudioPlayerOverlays"
import {
  formatAudioMarkerTime,
  resolveAudioMarkerTime,
} from "./audioRegionController"

interface UseAudioMarkerControllerOptions {
  /**
   * The element the add-marker button is positioned against. Hover coordinates
   * are measured on the waveform, which sits inside the player's padding, so
   * they have to be re-expressed against this element or the button lands off
   * the cursor by however much chrome separates the two.
   */
  anchorRef?: RefObject<HTMLDivElement | null>
  containerRef: RefObject<HTMLDivElement | null>
  wavesurfer: WaveSurfer | null
}

export function useAudioMarkerController({
  anchorRef,
  containerRef,
  wavesurfer,
}: UseAudioMarkerControllerOptions) {
  const [showAddMarkerModal, setShowAddMarkerModal] = useState(false)
  const [isHovering, setIsHovering] = useState(false)
  const [hoverTime, setHoverTime] = useState<number | null>(null)
  const [markers, setMarkers] = useState<AudioMarker[]>([])
  const [newMarkerLabel, setNewMarkerLabel] = useState("")
  const [buttonPosition, setButtonPosition] = useState<{
    x: number
    y: number
  } | null>(null)
  const [isButtonHovered, setIsButtonHovered] = useState(false)
  const [manualTime, setManualTime] = useState("")
  const [editingMarker, setEditingMarker] = useState<string | null>(null)
  const { trackEvent } = useAnalytics()

  const trackMarker = useCallback(
    (
      context: "audio_player_marker" | "audio_player_marker_edit",
      time: number,
    ) => {
      trackEvent(TRACKING_EVENTS.HUMAN_REVIEW.ANNOTATION_MADE, {
        context,
        marker_type: "interruption",
        marker_time: time,
      })
    },
    [trackEvent],
  )

  const resetMarkerForm = useCallback(() => {
    setNewMarkerLabel("")
    setShowAddMarkerModal(false)
    setHoverTime(null)
    setManualTime("")
    setEditingMarker(null)
  }, [])

  const addMarker = useCallback(() => {
    if (!wavesurfer || !newMarkerLabel.trim()) {
      return
    }

    const markerTime = resolveAudioMarkerTime({
      currentTime: wavesurfer.getCurrentTime(),
      duration: wavesurfer.getDuration(),
      hoverTime,
      manualTime,
    })
    const newMarker: AudioMarker = {
      id: `marker-${Date.now()}`,
      time: markerTime,
      label: newMarkerLabel.trim(),
      type: "interruption",
    }

    setMarkers((previous) => [...previous, newMarker])
    resetMarkerForm()
    trackMarker("audio_player_marker", markerTime)
  }, [
    hoverTime,
    manualTime,
    newMarkerLabel,
    resetMarkerForm,
    trackMarker,
    wavesurfer,
  ])

  const handleModalClose = useCallback(() => {
    setShowAddMarkerModal(false)
    setNewMarkerLabel("")
    setManualTime("")
    setEditingMarker(null)
  }, [])

  const handleWaveformMouseMove = useCallback(
    (event: MouseEvent) => {
      if (!wavesurfer || !containerRef.current) {
        return
      }

      const rect = containerRef.current.getBoundingClientRect()
      const x = event.clientX - rect.left
      const y = event.clientY - rect.top
      const target = event.target as HTMLElement
      const isOverFlag = target.closest("[data-marker-flag]")
      const isOverFlagContainer = target.closest(".flag-container")

      if (
        x >= 0 &&
        x <= rect.width &&
        y >= 0 &&
        y <= rect.height &&
        !isOverFlag &&
        !isOverFlagContainer
      ) {
        setIsHovering(true)
        setHoverTime(wavesurfer.getDuration() * (x / rect.width))
        const anchorRect = anchorRef?.current?.getBoundingClientRect() ?? rect
        setButtonPosition({
          x: event.clientX - anchorRect.left,
          y: rect.top - anchorRect.top,
        })
      } else if (isOverFlag && !event.buttons) {
        setIsHovering(false)
        setHoverTime(null)
        setButtonPosition(null)
      }
    },
    [anchorRef, containerRef, wavesurfer],
  )

  const handleWaveformMouseLeave = useCallback(() => {
    if (!isButtonHovered) {
      setIsHovering(false)
      setHoverTime(null)
      setButtonPosition(null)
    }
  }, [isButtonHovered])

  const handleButtonMouseEnter = useCallback(() => {
    setIsButtonHovered(true)
  }, [])

  const handleButtonMouseLeave = useCallback(() => {
    setIsButtonHovered(false)
    setIsHovering(false)
    setHoverTime(null)
    setButtonPosition(null)
  }, [])

  const handleAddMarkerAtPosition = useCallback(() => {
    if (!wavesurfer || hoverTime === null) {
      return
    }

    const newMarker: AudioMarker = {
      id: `marker-${Date.now()}`,
      time: hoverTime,
      label: `Interruption at ${formatTime(hoverTime)}`,
      type: "interruption",
    }
    setMarkers((previous) => [...previous, newMarker])
    trackMarker("audio_player_marker", hoverTime)
  }, [hoverTime, trackMarker, wavesurfer])

  const handleEditMarker = useCallback((marker: AudioMarker) => {
    setEditingMarker(marker.id)
    setNewMarkerLabel(marker.label)
    setManualTime(formatAudioMarkerTime(marker.time))
    setHoverTime(marker.time)
    setShowAddMarkerModal(true)
  }, [])

  const handleUpdateMarker = useCallback(() => {
    if (!wavesurfer || !newMarkerLabel.trim() || !editingMarker) {
      return
    }

    const markerTime = resolveAudioMarkerTime({
      currentTime: wavesurfer.getCurrentTime(),
      duration: wavesurfer.getDuration(),
      hoverTime,
      manualTime,
    })
    setMarkers((previous) =>
      previous.map((marker) =>
        marker.id === editingMarker
          ? { ...marker, time: markerTime, label: newMarkerLabel.trim() }
          : marker,
      ),
    )
    resetMarkerForm()
    trackMarker("audio_player_marker_edit", markerTime)
  }, [
    editingMarker,
    hoverTime,
    manualTime,
    newMarkerLabel,
    resetMarkerForm,
    trackMarker,
    wavesurfer,
  ])

  return {
    addMarker,
    buttonPosition,
    editingMarker,
    handleAddMarkerAtPosition,
    handleButtonMouseEnter,
    handleButtonMouseLeave,
    handleEditMarker,
    handleModalClose,
    handleUpdateMarker,
    handleWaveformMouseLeave,
    handleWaveformMouseMove,
    hoverTime,
    isButtonHovered,
    isHovering,
    manualTime,
    markers,
    newMarkerLabel,
    setHoverTime,
    setManualTime,
    setNewMarkerLabel,
    showAddMarkerModal,
  }
}
